import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionButton, PermissionGuard } from 'src/routes/components';
import { MoneyRequest, MoneyRequestPage } from 'src/pages/admin/services/moneyRequestApi';
import { RequestsTable } from '../../money-requests/RequestsTable';
import { useCurrentUserId, useRequestList } from '../../money-requests/useMoneyRequests';

const RESOURCE = 'world-cup-leaderboard-settlement';
const AMOUNT_INPUT = /^[0-9]*\.?[0-9]{0,6}$/;
const AMOUNT_VALID = /^\d+(\.\d{1,6})?$/;

export interface AmountRequestsApi {
  list: (params?: { page?: number; size?: number; status?: string }) => Promise<MoneyRequestPage>;
  approve: (id: string) => Promise<MoneyRequest>;
  reject: (id: string, reason: string) => Promise<MoneyRequest>;
  cancel: (id: string) => Promise<MoneyRequest>;
}

/** Pending amount override requests of the settlement screen: approve / reject / cancel. */
export function AmountRequestsPanel({
  api,
  reloadKey,
  onDecided,
}: {
  api: AmountRequestsApi;
  /** Bumped by the parent after it creates a request, so the list reloads. */
  reloadKey: number;
  /** Called after an approval, so the settlement ledger can be re-read. */
  onDecided: () => void;
}) {
  const t = useTranslate();
  const currentUserId = useCurrentUserId();
  const requests = useRequestList(api.list);
  const { load } = requests;

  // The parent bumps `reloadKey` after it creates a request; the first run is the initial load.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    load();
  }, [reloadKey, load]);

  return (
    <PermissionGuard
      anyOf={[
        { resource: RESOURCE, action: 'amount' },
        { resource: RESOURCE, action: 'approve-amount' },
      ]}
      fallback={<></>}
      loadingFallback={<></>}
    >
      <Stack spacing={1} sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1">
            {t('module.worldCupAmountRequests.title', { _: 'Amount override requests' })}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" onClick={requests.load}>
            {t('module.moneyRequests.reload', { _: 'Reload' })}
          </Button>
        </Stack>
        <Alert severity="info" variant="outlined">
          {t('module.worldCupAmountRequests.intro', {
            _: 'An amount override takes effect only when a different admin approves it. It changes the amount only; paying is a separate action.',
          })}
        </Alert>
        <RequestsTable
          rows={requests.rows}
          loading={requests.loading}
          currentUserId={currentUserId}
          busyId={requests.busyId}
          approvePermission={{ resource: RESOURCE, action: 'approve-amount' }}
          cancelPermission={{ resource: RESOURCE, action: 'amount' }}
          summary={(r) => (
            <span>
              entry #{r.payload.entryId}: set amount to <b>${r.payload.amountUsdc}</b>
            </span>
          )}
          onApprove={async (r) => {
            if (
              !window.confirm(
                t('module.worldCupAmountRequests.approveConfirm', {
                  _: 'Approve: set the amount of entry #%{entry} to $%{amount}?',
                  entry: r.payload.entryId,
                  amount: r.payload.amountUsdc,
                })
              )
            ) {
              return;
            }
            await requests.run(
              r.id,
              () => api.approve(r.id),
              t('module.moneyRequests.approved', { _: 'Approved' })
            );
            onDecided();
          }}
          onReject={(r, reason) =>
            requests.run(
              r.id,
              () => api.reject(r.id, reason),
              t('module.moneyRequests.rejected', { _: 'Rejected' })
            )
          }
          onCancel={(r) =>
            requests.run(
              r.id,
              () => api.cancel(r.id),
              t('module.moneyRequests.cancelled', { _: 'Cancelled' })
            )
          }
        />
      </Stack>
    </PermissionGuard>
  );
}

/** Dialog that creates an amount override REQUEST for one settlement entry. */
export function OverrideAmountDialog({
  entry,
  onClose,
  onRequest,
  onRequested,
}: {
  entry: { id: string; rank: number; address: string; amountUsdc: string };
  onClose: () => void;
  onRequest: (
    entryId: string,
    body: { amountUsdc: string; reason?: string }
  ) => Promise<MoneyRequest>;
  onRequested: () => void;
}) {
  const t = useTranslate();
  const notify = useNotify();
  const [amount, setAmount] = useState(String(entry.amountUsdc ?? ''));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onRequest(entry.id, { amountUsdc: amount, reason: reason.trim() || undefined });
      notify(
        t('module.worldCupAmountRequests.requested', {
          _: 'Override requested. A different admin must approve it before the amount changes.',
        }),
        { type: 'success' }
      );
      onRequested();
      onClose();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t('module.worldCupAmountRequests.overrideTitle', { _: 'Override amount' })} #{entry.rank}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            {entry.address}
          </Typography>
          <TextField
            size="small"
            required
            label="USDC"
            value={amount}
            onChange={(e) => AMOUNT_INPUT.test(e.target.value) && setAmount(e.target.value)}
            inputProps={{ inputMode: 'decimal' }}
            helperText={t('module.worldCupAmountRequests.zeroHint', {
              _: '0 is allowed: it pays this winner nothing.',
            })}
          />
          <TextField
            size="small"
            label={t('module.moneyRequests.reason', { _: 'Reason' })}
            value={reason}
            inputProps={{ maxLength: 500 }}
            onChange={(e) => setReason(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <PermissionButton resource={RESOURCE} action="amount">
          <Button
            variant="contained"
            disabled={saving || !AMOUNT_VALID.test(amount)}
            onClick={save}
          >
            {t('module.worldCupAmountRequests.submit', { _: 'Request override' })}
          </Button>
        </PermissionButton>
      </DialogActions>
    </Dialog>
  );
}
